import React, { useState, useEffect, useCallback, useMemo } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Grid, Paper, Typography } from "@mui/material";
import EditableTable from "../../../Controllers/EditableTable";
import { useSelector } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import service from "./../helpers/GifService";
import {
  applicantSelector,
  canEditSelector,
  isLeaderSelector,
} from "../../../store/applicantsSlice";
import { useLoading } from "../../../providers/loadingProvider";
import { useNotify } from "../../../providers/NotificationProvider";
import { pisColleaguesSelector } from "../../../store/proposalSlice";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  selectTableCell: {
    minWidth: 150,
  },
  textError: {
    color: "red",
  },
  editButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.yellow.hover,
    },
    color: theme.palette.gif.yellow.dark,
    backgroundColor: theme.palette.gif.yellow.color,
  },
  saveButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
  infoIcon: {
    marginLeft: 5,
  },
}));

export default function ProjectSchedules() {
  const classes = useStyles();
  const loading = useLoading();
  const notify = useNotify();
  const [proposalId, setproposalId] = useState("");
  //const [institutionOptions, setInstitutionOptions] = useState([]);
  const [projectsSchedules, setProjectsSchedules] = useState([]);
  const [proposalApplicants, setProposalApplicants] = useState([]);
  const applicant = useSelector(applicantSelector);
  const colleagues = useSelector(pisColleaguesSelector);
  const isLeader = useSelector(isLeaderSelector);
  const canEdit = useSelector(canEditSelector);

  const validateRow = (row) => {
    let errorList = [];
    if (
      row.taskdescription === null ||
      row.taskdescription === undefined ||
      row.taskdescription === ""
    )
      errorList.push("Research Task");
    if (
      row.institutionId === null ||
      row.institutionId === undefined ||
      row.institutionId === ""
    )
      errorList.push("Research Institution");
    if (
      row.startDate === null ||
      row.startDate === undefined ||
      row.startDate === ""
    )
      errorList.push("Beginning Date");
    if (row.endDate === null || row.endDate === undefined || row.endDate === "")
      errorList.push("End Date");
    if (
      row.explanatorynote === null ||
      row.explanatorynote === undefined ||
      row.explanatorynote === ""
    )
      errorList.push("Explanatory Note");

    if (errorList.length === 0) return true;

    notify.show(`Missing Fields:  ${errorList.join(", ")}`, {
      severity: "error",
    });

    return false;
  };

  useEffect(() => {
    service.getProposalData().then((proposal) => {
      setproposalId(proposal.id);
    });
  }, []);

  const setRow = (row) => {
    const loadingId = loading.addLoading("updating project schedule");
    const institutionId = colleagues.find(
      (c) => c.id === row.applicantGranteeId
    );
    row.institutionId = institutionId;
    service
      .udateProjectSchedule({
        projectsSchedules: row,
      })
      .then(() => {
        loading.removeLoading(loadingId);
        getProjectsSchedules();
      });
  };

  const addRow = async (row) => {
    const loadingId = loading.addLoading("creating project schedule");
    const institutionId = colleagues.find(
      (c) => c.id === row.applicantGranteeId
    );
    row.institutionId = institutionId;
    service
      .createProjectSchedule({
        applicantId: applicant.id,
        projectsSchedules: row,
      })
      .then(() => {
        loading.removeLoading(loadingId);
        getProjectsSchedules();
      });
  };

  const deleteRow = (projectsSchedulesId) => {
    const loadingId = loading.addLoading("removing project schedule");
    service.DeactivateProjectTimeSchedule({ projectsSchedulesId }).then(() => {
      loading.removeLoading(loadingId);
      getProjectsSchedules();
    });
  };

  // const fillInstitutionsOptions = useCallback(async () => {
  //   const loadingId = loading.addLoading("loading institutions");
  //   var institutions = await service.getAllInstitutions();
  //   setInstitutionOptions(
  //     institutions.map((i) => ({ ...i, id: i.institutionId, label: i.name }))
  //   );
  //   loading.removeLoading(loadingId);
  // }, [loading]);

  const fillProposalApplicants = useCallback(async () => {
    if (!proposalId || !colleagues) return [];
    setProposalApplicants(
      colleagues.map((c) => ({
        id: c.id,
        label: `${c.name} (${c.institutionName})`,
      }))
    );
  }, [proposalId, colleagues]);

  const getProjectsSchedules = useCallback(async () => {
    if (!proposalId) return;
    const loadingId = loading.addLoading("loading project schedule");
    var projects = await service.getProposalProjectTimeSchedule({
      proposalId,
    });
    projects.sort(
      (a, b) => new Date(a.startDate) - new Date(b.startDate)
    );
    setProjectsSchedules(projects);
    loading.removeLoading(loadingId);
  }, [loading, proposalId]);

  useEffect(() => {
    //fillInstitutionsOptions();
    getProjectsSchedules();
    fillProposalApplicants();
  }, [fillProposalApplicants, getProjectsSchedules]);
 
  const tableConfig = useMemo(
    () => ({
      idField: "projectTimescheduleId",
      disableEdit: !canEdit,
      editOnlyId: applicant.id,
      editOnlyIdFiled: "applicantGranteeId",
      columns: [
        {
          id: "taskdescription",
          label: "Research Task",
          field: "taskdescription",
        },
        // {
        //   id: "institutionId",
        //   label: "Research Institution",
        //   field: "institutionId",
        //   type: "options",
        //   options: institutionOptions,
        //   value: applicant.institutionId,
        //   width: 4,
        // },
        {
          id: "startDate",
          label: "Beginning Date",
          field: "startDate",
          type: "date",
        },
        {
          id: "endDate",
          label: "End Date",
          field: "endDate",
          type: "date",
        },
        {
          id: "applicantGranteeId",
          label: "Responsible PI",
          field: "applicantGranteeId",
          type: "options",
          options: proposalApplicants,
          disabled: !isLeader,
          value: applicant.id,
          width: 10,
        },
        {
          id: "explanatorynote",
          label: "Explanatory Note",
          type: "longText",
          field: "explanatorynote",
        },
      ],
    }),
    [canEdit, applicant.id, proposalApplicants, isLeader]
  );

  return (
    <>
      <Grid container>
        <Typography variant="subtitle2">Project Schedules</Typography>
        <InfoOutlinedIcon className={classes.infoIcon} color="primary" />
      </Grid>
      <Paper className={classes.root} elevation={0}>
        <EditableTable
          tableConfig={tableConfig}
          tableData={projectsSchedules}
          rowValidate={validateRow}
          rowCreated={addRow}
          rowUpdated={setRow}
          rowDeleted={deleteRow}
        />
      </Paper>
    </>
  );
}
